import { Indicator } from '@mantine/core';

export const CATALOG_FILTERS = {
  all: 'All Items',
  linked: 'Linked',
  partial: 'Partially Linked',
  unlinked: 'Unlinked',
};

export const CROP_VALIDATION_OPTIONS = {
  item: {
    minWidth: 1200,
    minHeight: 1200,
  },
  logo: {
    minWidth: 60,
    minHeight: 60,
  },
  cover: {
    minWidth: 1200,
    minHeight: 675,
  },
};

const CUBOH_CORPA_LINKS = {
  prod: 'https://corpa.cuboh.net',
  staging: 'https://corpa-staging.cuboh.net',
  default: 'https://corpa-feature.cuboh.net',
};

export const CUBOH_CORPA_LINK =
  CUBOH_CORPA_LINKS[import.meta.env.VITE_DEPLOY_ENV] || CUBOH_CORPA_LINKS.default;

export const LEGACY_DASHBOARD_URL =
  import.meta.env.VITE_DEPLOY_ENV === 'prod'
    ? 'https://dashboard.chownow.com'
    : `https://app-dashboard.${import.meta.env.VITE_DEPLOY_ENV}.svpn.chownow.com`;

export const ORDERING_SUBDOMAIN_PROD = 'order';

export const EDIT_TYPE = {
  items: 'Items',
  modifiers: 'Modifiers',
};

export const FULFILLMENT_METHODS = {
  curbside: 'curbside',
  delivery: 'delivery',
  dineIn: 'dine_in',
  pickup: 'pickup',
};

export const HCAFE_COMPANY_ID = '1';

export const ITEM_TYPE = {
  menuItem: 'Item',
  modifier: 'Modifier',
  size: 'Size',
};

export const ITEM_SIZE = {
  regular: 'Regular',
};

export const JOSIAHS_BAD_PASSWORDS = [
  'trustno1',
  'passw0rd',
  'p@ssword',
  'p@ssw0rd',
  'pa55word',
  '1q2w3e4r',
  '1q2w3e4r5t',
  'pa55w0rd',
  'p@55w0rd',
];

export const NOTIFICATION_VARIANT = {
  success: 'success',
  caution: 'caution',
  neutral: 'neutral',
};

export const ORDER_ACTION = {
  accept: 'accept',
  cancel: 'cancel',
};

export const ORDER_STATUS = {
  new: 'New',
  accepted: 'Accepted',
  canceled: 'Canceled',
  future: 'Future',
  submitted: 'Submitted',
  declined: 'Declined',
};

export const PAYMENT_PROCESSING_STRIPE_PARAM_STATUSES = {
  success: 'success',
  failure: 'failure',
};

export const PAYMENT_PROCESSING_STRIPE_RESPONSE_STATUSES = {
  completed: 'completed',
  payoutsDisabled: 'payouts_disabled',
  restrictedSoon: 'restricted_soon',
  unknown: 'unknown',
  unverified: 'unverified',
  verified: 'verified',
};

export const COVER_PHOTO_GUIDELINES_LINK =
  'https://support.chownow.com/restaurant/s/article/What-are-our-cover-photo-requirements-and-guidelines';
export const LOGO_PHOTO_GUIDELINES_LINK =
  'https://support.chownow.com/restaurant/s/article/What-are-our-logo-requirements-and-guidelines';
export const MENU_ITEM_PHOTO_GUIDELINES_LINK =
  'https://get.chownow.com/restaurant-support/what-are-our-photo-requirements-and-guidelines/';

export const PHOTO_TYPE = {
  logo: 'logo',
  cover: 'cover',
};

export const REFUND_STATUS = {
  pendingAutomaticProcessing: 'Pending Automatic Processing',
  pendingCustomerSupport: 'Pending Customer Support',
  pendingRestaurantFeedback: 'Pending Restaurant Feedback',
  automaticallyProcessed: 'Automatically Processed',
  processedManuallyByCs: 'Processed Manually By CS',
};

export const REFUND_TYPE = {
  full: { name: 'full', label: 'Full Refund' },
  item: { name: 'partial_by_item', label: 'Refund by Item' },
  itemV2: { name: 'partial_by_item_v2', label: 'Partial Refund' },
  amount: { name: 'partial_by_amount', label: 'Refund by Amount' },
};

export const TIMEOUTS = {
  closeSideBar: 300,
};

export const POS_COMPANIES = {
  square: { name: 'Square' },
  toast: { name: 'Toast' },
};

export const PAYMENT_PROCESSING_BADGES = {
  active: { label: 'active', variant: 'blue' },
  error: { label: 'error', variant: 'caution' },
  inactive: { label: 'inactive', variant: 'gray' },
  inReview: { label: 'pending', variant: 'outline' },
};

export const PAYMENT_PROCESSING_TITLES = {
  active: 'Your Stripe account is active',
  failure: 'Something went wrong with your Stripe activation',
  inactive: 'Your Stripe account is inactive',
  inReview: 'Stripe is reviewing your business information',
  noAccount: 'You do not have a Stripe account',
  restricted: 'Your Stripe account is restricted',
  warning: 'Your Stripe account needs updates',
};

export const NOTIFICATION_VARIANTS = {
  success: { label: 'success', icon: <Indicator variant="success" /> },
  neutral: { label: 'neutral', icon: <Indicator variant="active" /> },
  caution: { label: 'caution', icon: <Indicator variant="caution" /> },
};

export const CHOWNOW_ORDER_TYPES_LABEL = 'OBN, Direct Ordering, and ChowNow Marketplace';

export const PAYMENT_PROCESSING_CONTENT = {
  active:
    'You are able to accept and process orders. You can still make updates to your Stripe account information anytime.',
  failure:
    'Please retry activating your Stripe account. If the error persists, contact customer support. Use the button below to resubmit your details.',
  inactive:
    'To accept and process orders, you need an active Stripe account. Stripe is a secure payment processor that powers transactions on our platform. Use the button to submit your details and activate your account.\n\nHave your Social Security Number and a Photo ID ready.',
  inReview:
    'We will let you know if you need to provide any additional details. You can check your status by clicking the button below.',
  restricted: `You will be unable to take orders or process payments on ${CHOWNOW_ORDER_TYPES_LABEL} until you make the necessary updates on your account.\n\nThe button below will link out to make these updates.`,
  warning:
    'If you do not update your information, you will be unable to take orders and process payments on your ordering platform. Use the button below to manage your details.',
};

export const PAYMENT_PROCESSING_ACTION_BUTTON = {
  activateStripe: 'Activate Stripe',
  checkStatus: 'Check Status',
  manageStripe: 'Manage Stripe',
};

export const SUPPORT_CONTACT_URL = 'https://get.chownow.com/contact/';
export const SUPPORT_URL = 'https://support.chownow.com/restaurant/s/';
export const SUPPORT_PHONE = '1-888-707-2469';
export const KNOWLEDGE_BASE_URL =
  'https://get.chownow.com/restaurant-support/chownow-hub-overview/';
export const MARKETING_STORE_URL = 'https://www.chownowstore.com/';
export const CHOWNOW_BUSINESS_MANAGER_APP_URL =
  'https://apps.apple.com/us/app/chownow-business-manager/id581010873';

export const MENU_EDITOR = {
  cuboh: 'CUBOH',
  toast: 'TOAST',
  legacy: 'LEGACY',
  middleware: 'MIDDLEWARE',
};

export const PRICING_PLANS = {
  core: 'CORE',
  hub: 'HUB',
  noPlan: 'NO PLAN',
  premier: 'PREMIER',
  pro: 'PRO',
};

export const CANADIAN_REGIONS = [
  { label: 'Alberta', value: 'AB' },
  { label: 'British Columbia', value: 'BC' },
  { label: 'Manitoba', value: 'MB' },
  { label: 'New Brunswick', value: 'NB' },
  { label: 'Newfoundland And Labrador', value: 'NL' },
  { label: 'Northwest Territories', value: 'NT' },
  { label: 'Nova Scotia', value: 'NS' },
  { label: 'Nunavut', value: 'NU' },
  { label: 'Ontario', value: 'ON' },
  { label: 'Prince Edward Island', value: 'PE' },
  { label: 'Quebec', value: 'QC' },
  { label: 'Saskatchewan', value: 'SK' },
  { label: 'Yukon Territory', value: 'YT' },
];

export const USA_REGIONS = [
  { label: 'Alabama', value: 'AL' },
  { label: 'Alaska', value: 'AK' },
  { label: 'American Samoa', value: 'AS' },
  { label: 'Arizona', value: 'AZ' },
  { label: 'Arkansas', value: 'AR' },
  { label: 'California', value: 'CA' },
  { label: 'Colorado', value: 'CO' },
  { label: 'Connecticut', value: 'CT' },
  { label: 'Delaware', value: 'DE' },
  { label: 'District Of Columbia', value: 'DC' },
  { label: 'Florida', value: 'FL' },
  { label: 'Georgia', value: 'GA' },
  { label: 'Guam', value: 'GU' },
  { label: 'Hawaii', value: 'HI' },
  { label: 'Idaho', value: 'ID' },
  { label: 'Illinois', value: 'IL' },
  { label: 'Indiana', value: 'IN' },
  { label: 'Iowa', value: 'IA' },
  { label: 'Kansas', value: 'KS' },
  { label: 'Kentucky', value: 'KY' },
  { label: 'Louisiana', value: 'LA' },
  { label: 'Maine', value: 'ME' },
  { label: 'Maryland', value: 'MD' },
  { label: 'Massachusetts', value: 'MA' },
  { label: 'Michigan', value: 'MI' },
  { label: 'Minnesota', value: 'MN' },
  { label: 'Mississippi', value: 'MS' },
  { label: 'Missouri', value: 'MO' },
  { label: 'Montana', value: 'MT' },
  { label: 'Nebraska', value: 'NE' },
  { label: 'Nevada', value: 'NV' },
  { label: 'New Hampshire', value: 'NH' },
  { label: 'New Jersey', value: 'NJ' },
  { label: 'New Mexico', value: 'NM' },
  { label: 'New York', value: 'NY' },
  { label: 'North Carolina', value: 'NC' },
  { label: 'North Dakota', value: 'ND' },
  { label: 'Northern Mariana Islands', value: 'MP' },
  { label: 'Ohio', value: 'OH' },
  { label: 'Oklahoma', value: 'OK' },
  { label: 'Oregon', value: 'OR' },
  { label: 'Pennsylvania', value: 'PA' },
  { label: 'Puerto Rico', value: 'PR' },
  { label: 'Rhode Island', value: 'RI' },
  { label: 'South Carolina', value: 'SC' },
  { label: 'South Dakota', value: 'SD' },
  { label: 'Tennessee', value: 'TN' },
  { label: 'Texas', value: 'TX' },
  { label: 'United States Minor Outlying Islands', value: 'UM' },
  { label: 'Utah', value: 'UT' },
  { label: 'Vermont', value: 'VT' },
  { label: 'Virgin Islands', value: 'VI' },
  { label: 'Virginia', value: 'VA' },
  { label: 'Washington', value: 'WA' },
  { label: 'West Virginia', value: 'WV' },
  { label: 'Wisconsin', value: 'WI' },
  { label: 'Wyoming', value: 'WY' },
];

export const COUNTRIES = {
  USA: {
    alpha3: 'USA',
    regions: USA_REGIONS,
    postalCodeLabel: 'Zip',
    postalCodeLength: 5,
  },
  CANADA: {
    alpha3: 'CAN',
    regions: CANADIAN_REGIONS,
    postalCodeLabel: 'Postal Code',
    postalCodeLength: 6,
  },
};

export const HERMOSA_MARKETPLACE_IDS = {
  direct: '',
  yelp: '1',
  marketplace: '2',
  google: '3',
};

export const ORDER_SOURCE = {
  android: 'android',
  androidV3: 'android_v3',
  apple: 'apple',
  direct: 'direct',
  directWeb: 'direct_web',
  directWebMobile: 'direct_web_mobile',
  facebook: 'facebook',
  google: 'google',
  ios: 'ios',
  iosV3: 'ios_v3',
  marketplace: 'marketplace',
  mobile: 'mobile',
  nextdoor: 'nextdoor',
  snapchat: 'snapchat',
  tripadvisor: 'tripadvisor',
  unknown: 'unknown',
  yelp: 'yelp',
  cateringOrder: 'cateringOrder',
};

export const ORDER_SOURCE_LABEL = {
  apple: 'OBN - Apple',
  facebook: 'OBN - Facebook',
  google: 'OBN - Google',
  nextdoor: 'OBN - Nextdoor',
  snapchat: 'OBN - Snapchat',
  tripadvisor: 'OBN - Tripadvisor',
  website: 'Website',
  yelp: 'OBN - Yelp',
};

export const OBN_SOURCE_TOOLTIP_NAMES = {
  [ORDER_SOURCE.apple]: 'Apple',
  [ORDER_SOURCE.facebook]: 'Facebook',
  [ORDER_SOURCE.google]: 'Google',
  [ORDER_SOURCE.nextdoor]: 'Nextdoor',
  [ORDER_SOURCE.snapchat]: 'Snapchat',
  [ORDER_SOURCE.tripadvisor]: 'Tripadvisor',
  [ORDER_SOURCE.yelp]: 'Yelp',
};

export const SETTINGS_ALERT_MESSAGE =
  'These settings only apply to your ChowNow ordering channels.';

export const FEATURE_FLAGS = {
  channelManager: 'channelManager',
  csvImport: 'campaignCsvUpload',
  directOrderCommission: 'directOrderCommission',
  marketingAiRecommendations: 'marketingAiRecommendations',
  marketplaceRestoCommission: 'marketplaceRestoCommission',
  menuPhotoEnhancementTool: 'menuPhotoEnhancementTool',
  posMenuSettings: 'posMenuSettings',
  pushNotification: 'pushNotification',
  rpDashAgent: 'rpDashAgent',
};

export const MAX_PAGE_SIZE = 100;

export const WEBSITE_ACCESS_PARTIES = {
  CHOWNOW: 'chownow',
  THIRD_PARTY: 'third_party_developer',
  RESTO_PARTNER: 'restaurant_partner',
};

export const ORDERING_MODAL_SCRIPT_SRC =
  'https://cf.chownowcdn.com/latest/static/integrations/ordering-modal.min.js';

export const WEBSITE_INTEGRATION_INSTRUCTIONS =
  'https://get.chownow.com/restaurant-support/how-to-perform-a-basic-integration/';

export const ORDER_TYPE = {
  TAKEOUT: 'takeout',
  CATERING: 'catering',
};

export const DEFAULT_THEME = {
  label: 'Vanilla',
  value: '#D9D8D7',
};

export const GOOGLE_BUSINESS_LOCATIONS_URL = 'https://business.google.com/locations';
export const GOOGLE_BUSINESS_PROFILE_URL = 'https://business.google.com/en-all/business-profile/';
